import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { resolveEducationMediaUrl } from '../../lib/educationMedia';

const formatDuration = (seconds) => {
  if (!seconds || Number.isNaN(Number(seconds))) return null;
  const totalSeconds = Number(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  const hours = Math.floor(minutes / 60);
  const hourMinutes = minutes % 60;
  return `${hours}:${String(hourMinutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const getMediaIcon = (mediaType) => {
  switch (mediaType) {
    case 'video':
      return FiIcons.FiVideo;
    case 'audio':
      return FiIcons.FiVolume2;
    case 'image':
    case 'thumbnail':
      return FiIcons.FiImage;
    case 'document':
      return FiIcons.FiFileText;
    case 'interactive':
      return FiIcons.FiMonitor;
    case 'transcript':
    case 'caption':
      return FiIcons.FiAlignLeft;
    default:
      return FiIcons.FiPlayCircle;
  }
};

const getEmbeddableVideoUrl = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();

    if (host === 'play.gumlet.io' && parsed.pathname.startsWith('/embed/')) {
      return parsed.toString();
    }

    if (host === 'gumlet.tv') {
      const match = parsed.pathname.match(/^\/watch\/([^/]+)/);
      if (match?.[1]) {
        return `https://play.gumlet.io/embed/${match[1]}${parsed.search || ''}`;
      }
    }
  } catch (err) {
    return null;
  }

  return null;
};

const normalizeLegacyMedia = (resource) => {
  if (!resource) return [];
  const items = [];

  if (resource.video_url) {
    items.push({
      id: 'legacy-video',
      media_type: 'video',
      source_type: 'external_url',
      title: resource.title,
      media_url: resource.video_url,
      is_primary: true,
      display_priority: 10
    });
  }

  if (resource.media_url && resource.media_url !== resource.video_url) {
    items.push({
      id: 'legacy-media',
      media_type: resource.resource_type === 'downloadable_pdf' ? 'document' : resource.resource_type,
      source_type: 'external_url',
      title: resource.title,
      media_url: resource.media_url,
      is_primary: items.length === 0,
      display_priority: 20
    });
  }

  if (resource.thumbnail_url && !items.some((item) => item.thumbnail_url)) {
    items.push({
      id: 'legacy-thumbnail',
      media_type: 'thumbnail',
      source_type: 'external_url',
      title: `${resource.title} thumbnail`,
      media_url: resource.thumbnail_url,
      alt_text: resource.title,
      display_priority: 30
    });
  }

  return items;
};

const ResourceMediaPanel = ({ resource, mediaItems = [] }) => {
  const [showTranscript, setShowTranscript] = useState(false);
  const items = useMemo(() => {
    const combined = [...mediaItems, ...normalizeLegacyMedia(resource)];
    const byKey = new Map();

    combined.forEach((item) => {
      const key = [
        item.media_type,
        resolveEducationMediaUrl(item) || '',
        item.embed_url || '',
        item.transcript_text || ''
      ].join('|');
      if (!byKey.has(key)) byKey.set(key, item);
    });

    return Array.from(byKey.values()).sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return (a.display_priority || 100) - (b.display_priority || 100);
    });
  }, [mediaItems, resource]);

  if (items.length === 0) return null;

  const primary = items.find((item) => item.is_primary) || items[0];
  const supportingItems = items.filter((item) => item.id !== primary.id);
  const transcriptItems = items.filter((item) => item.transcript_text);
  const primaryDuration = formatDuration(primary.duration_seconds);
  const primaryMediaUrl = resolveEducationMediaUrl(primary);
  const primaryUrl = primary.embed_url || primaryMediaUrl;
  const primaryVideoEmbedUrl = primary.media_type === 'video'
    ? primary.embed_url || getEmbeddableVideoUrl(primaryMediaUrl)
    : null;

  const renderPrimaryMedia = () => {
    if (primary.media_type === 'video' && primaryVideoEmbedUrl) {
      return (
        <iframe
          title={primary.title || resource.title}
          src={primaryVideoEmbedUrl}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    if (primary.media_type === 'video' && primaryMediaUrl) {
      return (
        <video
          controls
          poster={primary.thumbnail_url || resource.thumbnail_url || undefined}
          className="h-full w-full bg-slate-950"
        >
          <source src={primaryMediaUrl} type={primary.mime_type || undefined} />
          {primary.caption_url && <track kind="captions" src={primary.caption_url} label="Captions" />}
        </video>
      );
    }

    if (primary.media_type === 'audio' && primaryMediaUrl) {
      return (
        <div className="flex h-full flex-col justify-center gap-5 bg-slate-900 p-8">
          <div className="flex items-center gap-3 text-white">
            <SafeIcon icon={FiIcons.FiVolume2} className="h-7 w-7" />
            <div>
              <p className="text-sm font-bold">{primary.title || resource.title}</p>
              {primaryDuration && <p className="text-xs text-slate-300">{primaryDuration}</p>}
            </div>
          </div>
          <audio controls className="w-full">
            <source src={primaryMediaUrl} type={primary.mime_type || undefined} />
          </audio>
        </div>
      );
    }

    if ((primary.media_type === 'image' || primary.media_type === 'thumbnail') && primaryMediaUrl) {
      return (
        <img
          src={primaryMediaUrl}
          alt={primary.alt_text || resource.title}
          className="h-full w-full object-cover"
        />
      );
    }

    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-900 p-8 text-center text-white">
        <SafeIcon icon={getMediaIcon(primary.media_type)} className="mb-4 h-10 w-10 text-blue-200" />
        <p className="text-sm font-bold">{primary.title || resource.title}</p>
        {primary.description && <p className="mt-2 max-w-lg text-sm text-slate-300">{primary.description}</p>}
        {primaryUrl && (
          <a
            href={primaryUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center rounded-md bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-slate-900"
          >
            <SafeIcon icon={primary.is_downloadable ? FiIcons.FiDownload : FiIcons.FiExternalLink} className="mr-2 h-4 w-4" />
            {primary.is_downloadable ? 'Download' : 'Open'}
          </a>
        )}
      </div>
    );
  };

  return (
    <div className="border-b border-slate-100 bg-slate-50">
      <div className="aspect-video overflow-hidden bg-slate-900">
        {renderPrimaryMedia()}
      </div>

      <div className="space-y-4 p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-700">
                <SafeIcon icon={getMediaIcon(primary.media_type)} className="mr-1.5 h-3.5 w-3.5" />
                {primary.media_type}
              </span>
              {primaryDuration && <span className="text-xs font-medium text-slate-500">{primaryDuration}</span>}
            </div>
            {(primary.title || primary.description) && (
              <div className="mt-3">
                {primary.title && <p className="text-sm font-bold text-slate-900">{primary.title}</p>}
                {primary.description && <p className="mt-1 text-sm leading-relaxed text-slate-600">{primary.description}</p>}
              </div>
            )}
          </div>

          {primaryUrl && !['video', 'audio', 'image', 'thumbnail'].includes(primary.media_type) && (
            <a
              href={primaryUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-700 hover:border-blue-200 hover:text-blue-700"
            >
              <SafeIcon icon={primary.is_downloadable ? FiIcons.FiDownload : FiIcons.FiExternalLink} className="mr-2 h-4 w-4" />
              {primary.is_downloadable ? 'Download' : 'Open'}
            </a>
          )}
        </div>

        {(supportingItems.length > 0 || transcriptItems.length > 0) && (
          <div className="grid gap-3 sm:grid-cols-2">
            {supportingItems.map((item) => {
              const itemUrl = item.embed_url || resolveEducationMediaUrl(item) || item.caption_url;
              return (
                <div key={item.id} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex items-start gap-3">
                    <SafeIcon icon={getMediaIcon(item.media_type)} className="mt-0.5 h-4 w-4 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{item.media_type}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{item.title || item.description || resource.title}</p>
                      {item.description && <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.description}</p>}
                      {itemUrl && (
                        <a
                          href={itemUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-3 inline-flex items-center text-xs font-bold uppercase tracking-widest text-blue-600"
                        >
                          <SafeIcon icon={item.is_downloadable ? FiIcons.FiDownload : FiIcons.FiExternalLink} className="mr-1.5 h-3.5 w-3.5" />
                          {item.is_downloadable ? 'Download' : 'Open'}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {transcriptItems.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setShowTranscript((current) => !current)}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-slate-700"
            >
              Transcript
              <SafeIcon icon={showTranscript ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="h-4 w-4" />
            </button>
            {showTranscript && (
              <div className="border-t border-slate-100 px-4 py-4">
                {transcriptItems.map((item) => (
                  <div key={`${item.id}-transcript`} className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                    {item.transcript_text}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceMediaPanel;
